import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { updatePassword } from 'aws-amplify/auth'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import Button from '../components/Button'
import Password from '../components/Password'
import { PasswordConfirmationSchema, PasswordSchema } from '../utils/schemas'

export const ChangePassword = () => {
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const ValidationSchema = Yup.object().shape({
    'old-password': Yup.string().required('Old password is required'),
    'new-password': PasswordSchema,
    'confirm-password': PasswordConfirmationSchema('new-password'),
  })

  const changePassword = async ({
    'old-password': oldPassword,
    'new-password': newPassword,
    'confirm-password': passwordConfirmation,
  }: {
    'old-password': string
    'new-password': string
    'confirm-password': string
  }) => {
    if (newPassword !== passwordConfirmation) {
      setLoading(false)
      setErrorMessage('Passwords do not match')
      setHasError(true)
    }
    setLoading(true)
    try {
      await updatePassword({ oldPassword, newPassword })
      navigate('/')
    } catch (e: any) {
      if (e.name === 'NotAuthorizedException') {
        setErrorMessage('Your old password is invalid')
      } else {
        setErrorMessage(e.message)
      }
      setHasError(true)
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog
        open={hasError}
        onClose={() => setHasError(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto rounded-lg p-10 bg-primary-50 w-2/6">
            <DialogTitle>Error while resetting your password</DialogTitle>
            <Description className="py-4">{errorMessage}</Description>

            <button
              className="ircam-button button button-regular button-dark block mx-auto px-4 py-2"
              type="button"
              onClick={() => setHasError(false)}
            >
              <div className="button-label">OK</div>
            </button>
          </DialogPanel>
        </div>
      </Dialog>
      <div className="w-4/6 mx-auto mt-28">
        <h1 className="text-4xl text-center my-8 font-light">
          Change your password
        </h1>
        <Formik
          initialValues={{
            'old-password': '',
            'new-password': '',
            'confirm-password': '',
          }}
          validationSchema={ValidationSchema}
          onSubmit={changePassword}
        >
          {({ errors, touched }) => (
            <div className="w-full my-4">
              <Form>
                <div className="my-8">
                  <label htmlFor="old-password" className="text-sm">
                    Old password
                    <Field
                      as={Password}
                      name="old-password"
                      id="old-password"
                    />
                    {errors && touched['old-password'] && (
                      <span className="text-ircamprimary-accent">
                        {errors && errors['old-password']}
                      </span>
                    )}
                  </label>
                </div>
                <div className="my-8">
                  <label htmlFor="new-password" className="text-sm">
                    New password
                    <Field
                      as={Password}
                      name="new-password"
                      id="new-password"
                      checkValidity
                    />
                    <ErrorMessage
                      name="new-password"
                      component="span"
                      className="text-ircamprimary-accent"
                    />
                  </label>
                </div>
                <div className="my-8">
                  <label htmlFor="confirm-password" className="text-sm">
                    Confirm new password
                    <Field
                      as={Password}
                      name="confirm-password"
                      id="confirm-password"
                    />
                    <ErrorMessage
                      name="confirm-password"
                      component="span"
                      className="text-ircamprimary-accent"
                    />
                  </label>
                </div>
                <div className="my-2">
                  <button
                    type="submit"
                    className="w-full button button-large button-dark py-2 px-4"
                  >
                    <div className="button button-label">Change password</div>
                  </button>
                </div>
                <div className="my-2">
                  <Button
                    as={Link}
                    to="/login"
                    label="Cancel"
                    dark={false}
                    className="w-full my-8 py-2 text-center"
                    disabled={loading}
                  />
                </div>
              </Form>
            </div>
          )}
        </Formik>
      </div>
    </>
  )
}
